import { createI18n } from "vue-i18n";
import enGB from "./langs/en-GB";
import itIT from './langs/it-IT';

export type LocaleType = "it-IT" | "en-GB";

// Locali supportate dall'applicazione
export const SUPPORTED_LOCALES: LocaleType[] = ['it-IT', 'en-GB'];

// Locale di default
export const DEFAULT_LOCALE: LocaleType = "en-GB";

/**
 * Trova la locale più vicina tra quelle supportate in base alla lingua del browser
 *
 * Logica di matching:
 * 1. Cerca match esatto (es: "it-IT" -> "it-IT")
 * 2. Cerca match per lingua base (es: "en-US" -> "en-GB", "it-CH" -> "it-IT")
 * 3. Ritorna la locale di default (it-IT)
 *
 * @param browserLang - La lingua del browser (es: "en-US", "it-IT", "fr-FR")
 * @returns La locale supportata più vicina
 */
export function getClosestSupportedLocale(browserLang: string): LocaleType {
    if (!browserLang) {
        return DEFAULT_LOCALE;
    }

    const normalized = browserLang.trim();

    // 1. Cerca match esatto (case-insensitive)
    const exactMatch = SUPPORTED_LOCALES.find(
        (locale) => locale.toLowerCase() === normalized.toLowerCase()
    );
    if (exactMatch) {
        return exactMatch;
    }

    // 2. Estrai la lingua base (es: "en" da "en-US")
    const baseLang = normalized.split("-")[0].toLowerCase();

    // Cerca locale supportata con la stessa lingua base
    const langMatch = SUPPORTED_LOCALES.find((locale) => {
        const localeBase = locale.split("-")[0].toLowerCase();
        return localeBase === baseLang;
    });

    if (langMatch) {
        return langMatch;
    }

    // 3. Nessun match trovato, usa default
    return DEFAULT_LOCALE;
}

/**
 * Ottiene la lingua del browser e la converte in una locale supportata
 */
export function getBrowserLocale(): LocaleType {
    const browserLang =
        navigator.language ||
        (navigator as any).userLanguage ||
        DEFAULT_LOCALE;

    return getClosestSupportedLocale(browserLang);
}

const i18n = createI18n({
    legacy: false,
    locale: getBrowserLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    messages: {
        'it-IT': itIT.messages,
        'en-GB': enGB.messages
    }
});

export default i18n;
